import { useEffect, useMemo, useState, type FormEvent } from "react";
import { DEFAULT_TEMPLATE_BY_CONTENT_TYPE } from "@contentgen/shared";
import { RefThumbnail } from "../components/RefThumbnail.js";
import { RoutePicker } from "../components/RoutePicker.js";
import { StatusChip } from "../components/StatusChip.js";
import { TemplateEditor } from "../components/TemplateEditor.js";
import { api, apiUpload, ApiError } from "../lib/api.js";
import { clearOnEscape } from "../lib/clearOnEscape.js";
import { summarizeEstimate, type EstimateSummary } from "../lib/estimateSummary.js";
import { hashForRoute } from "../lib/hashRoute.js";
import { parseItemPaste } from "../lib/itemImport.js";
import type {
  ContentType,
  Deliverable,
  EstimateResponse,
  Item,
  Project,
  PromptTemplate,
  ReferenceAsset,
} from "../lib/types.js";
import { useDebounced } from "../lib/useDebounced.js";
import { usePolling } from "../lib/usePolling.js";
import { ROUTE_TITLES, formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";

const CONTENT_TYPES: ContentType[] = ["text", "image", "svg", "audio", "video"];

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const btnGhost =
  "shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 " +
  "disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

/** A projected cost: cents get 2 decimals, sub-cent amounts get 4 so a
 *  $0.0005-per-image local estimate does not render as "$0.00". */
function fmtUsd(usd: number): string {
  return `$${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

function ItemsTab({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<Item[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const debouncedFilter = useDebounced(filter);

  const [showAdd, setShowAdd] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [templates, setTemplates] = useState<PromptTemplate[]>([]);

  const [showDeliverableForm, setShowDeliverableForm] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newContentType, setNewContentType] = useState<ContentType>("text");
  const [newTemplateId, setNewTemplateId] = useState<string>(DEFAULT_TEMPLATE_BY_CONTENT_TYPE.text);
  const [newVariants, setNewVariants] = useState(1);
  const [deliverableBusy, setDeliverableBusy] = useState(false);
  const [deliverableError, setDeliverableError] = useState<string | null>(null);

  const [generateBusy, setGenerateBusy] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<EstimateSummary | null>(null);
  const [estimateBusy, setEstimateBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  async function load(cursor: string | undefined, replace: boolean) {
    setLoading(true);
    setError(null);
    try {
      const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      const page = await api<{ items: Item[]; nextCursor?: string }>(`/api/projects/${projectId}/items${query}`);
      setItems((prev) => (replace ? page.items : [...prev, ...page.items]));
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load items.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(undefined, true);
    // Re-run only when the project changes, not on every `load` identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    void api<{ items: PromptTemplate[] }>("/api/templates")
      .then((res) => setTemplates(res.items))
      .catch(() => setTemplates([]));
  }, []);

  // Keep the items list live while any deliverable on it is still generating.
  const anyGenerating = useMemo(
    () => items.some((i) => Object.values(i.deliverableSummary).some((s) => s.status === "generating")),
    [items],
  );
  usePolling(() => load(undefined, true), { enabled: anyGenerating });

  const templatesForNew = useMemo(
    () => templates.filter((t) => t.contentType === newContentType),
    [templates, newContentType],
  );

  const visible = useMemo(() => {
    const q = debouncedFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.title.toLowerCase().includes(q) || i.brief.toLowerCase().includes(q));
  }, [items, debouncedFilter]);

  async function handleAddItems(e: FormEvent) {
    e.preventDefault();
    const parsed = parseItemPaste(pasteText);
    if (parsed.length === 0) {
      setAddError("Paste at least one item first.");
      return;
    }
    setAddBusy(true);
    setAddError(null);
    try {
      const res = await api<{ items: Item[] }>(`/api/projects/${projectId}/items`, {
        method: "POST",
        json: { items: parsed },
      });
      setItems((prev) => [...res.items, ...prev]);
      setPasteText("");
      setShowAdd(false);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Could not add those items.");
    } finally {
      setAddBusy(false);
    }
  }

  async function handleAddDeliverableToAll(e: FormEvent) {
    e.preventDefault();
    const key = newKey.trim();
    if (!key) return;
    setDeliverableBusy(true);
    setDeliverableError(null);
    try {
      const targets = items.filter((i) => !i.deliverableSummary[key]);
      await Promise.all(
        targets.map((i) =>
          api(`/api/projects/${projectId}/items/${i.itemId}/deliverables`, {
            method: "POST",
            json: {
              key,
              contentType: newContentType,
              templateId: newTemplateId || undefined,
              variantCount: newVariants,
            },
          })
        )
      );
      await load(undefined, true);
      setShowDeliverableForm(false);
      setNewKey("");
    } catch (err) {
      setDeliverableError(err instanceof Error ? err.message : "Could not add the deliverable to every item.");
    } finally {
      setDeliverableBusy(false);
    }
  }

  // Step 1: ask the server what generating every pending deliverable would
  // cost. Nothing is queued here - the confirm button below is the only thing
  // that starts jobs (G-5).
  async function handleRequestEstimate() {
    setEstimateBusy(true);
    setGenerateError(null);
    setEstimate(null);
    try {
      const res = await api<EstimateResponse>(`/api/projects/${projectId}/generate-estimate`, {
        method: "POST",
        json: {},
      });
      setEstimate(summarizeEstimate(res));
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : "Could not estimate the cost.");
    } finally {
      setEstimateBusy(false);
    }
  }

  // Step 2: the sequential generate loop, reporting progress and surfacing the
  // first typed error code (a NO_ENABLED_ROUTE mid-batch stops it and says
  // which content type is unserved - that text is in the server's message).
  async function handleConfirmGenerate() {
    const total = estimate?.count ?? 0;
    setGenerateBusy(true);
    setGenerateError(null);
    setProgress({ done: 0, total });
    let done = 0;
    try {
      for (const item of items) {
        const page = await api<{ items: Deliverable[] }>(
          `/api/projects/${projectId}/items/${item.itemId}/deliverables`
        );
        const pending = page.items.filter((d) => d.status === "pending");
        for (const d of pending) {
          await api(`/api/deliverables/${d.deliverableId}/generate`, { method: "POST", json: {} });
          done += 1;
          setProgress({ done, total: Math.max(total, done) });
        }
      }
      setEstimate(null);
      await load(undefined, true);
    } catch (err) {
      if (err instanceof ApiError && err.code) {
        setGenerateError(`Stopped after ${done} of ${total}: ${err.code} - ${err.message}`);
      } else {
        setGenerateError(
          err instanceof Error ? err.message : "Could not generate every pending deliverable."
        );
      }
    } finally {
      setGenerateBusy(false);
      setProgress(null);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btnPrimary} onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Cancel" : "Add items"}
        </button>
        <button type="button" className={btnGhost} onClick={() => setShowDeliverableForm((v) => !v)}>
          {showDeliverableForm ? "Cancel" : "Add deliverable to all"}
        </button>
        <button
          type="button"
          className={btnGhost}
          disabled={generateBusy || estimateBusy || items.length === 0}
          onClick={() => void handleRequestEstimate()}
        >
          {estimateBusy ? "Estimating..." : generateBusy ? "Generating..." : "Generate all pending"}
        </button>

        <div className="relative ml-auto min-w-56 flex-1">
          <input
            className={inputCls}
            placeholder="Filter items..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={clearOnEscape(filter, setFilter)}
          />
          {filter && (
            <button
              type="button"
              aria-label="Clear filter"
              onClick={() => setFilter("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {showAdd && (
        <form onSubmit={(e) => void handleAddItems(e)} className="mt-3 space-y-2 rounded-md border border-gray-200 p-3 dark:border-gray-800">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            One title per line, a CSV with a <code>title,brief</code> header, or a JSON array.
          </p>
          <textarea
            rows={6}
            className={`${inputCls} font-mono text-xs`}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Squat\nDeadlift\nBench Press"}
          />
          {addError && <p className="text-sm text-red-600 dark:text-red-400">{addError}</p>}
          <button type="submit" className={btnPrimary} disabled={addBusy}>
            {addBusy ? "Adding..." : `Add ${parseItemPaste(pasteText).length || ""} item(s)`}
          </button>
        </form>
      )}

      {showDeliverableForm && (
        <form
          onSubmit={(e) => void handleAddDeliverableToAll(e)}
          className="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-gray-200 p-3 dark:border-gray-800"
        >
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Key</label>
            <input className={inputCls} value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="instructions" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Content type</label>
            <select
              className={inputCls}
              value={newContentType}
              onChange={(e) => {
                const ct = e.target.value as ContentType;
                setNewContentType(ct);
                setNewTemplateId(DEFAULT_TEMPLATE_BY_CONTENT_TYPE[ct]);
              }}
            >
              {CONTENT_TYPES.map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Template</label>
            <select
              className={inputCls}
              value={newTemplateId}
              onChange={(e) => setNewTemplateId(e.target.value)}
            >
              {templatesForNew.length === 0 && (
                <option value={newTemplateId}>{newTemplateId || "(default)"}</option>
              )}
              {templatesForNew.map((t) => (
                <option key={t.templateId} value={t.templateId}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Variants</label>
            <input
              type="number"
              min={1}
              max={4}
              className="w-20 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              value={newVariants}
              onChange={(e) => setNewVariants(Math.min(4, Math.max(1, Number(e.target.value) || 1)))}
            />
          </div>
          <button type="submit" className={btnPrimary} disabled={deliverableBusy || !newKey.trim()}>
            {deliverableBusy ? "Adding..." : "Add to all items missing it"}
          </button>
          {deliverableError && <p className="w-full text-sm text-red-600 dark:text-red-400">{deliverableError}</p>}
        </form>
      )}

      {estimate && (
        <div className="mt-3 space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
          {estimate.leadWithCost ? (
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              This will cost about {fmtUsd(estimate.totalUsd)} across {estimate.count} deliverable(s).
            </p>
          ) : (
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              Generate {estimate.count} deliverable(s){" "}
              <span className="font-normal text-gray-500 dark:text-gray-400">
                - est. {fmtUsd(estimate.totalUsd)}
              </span>
            </p>
          )}
          {estimate.rows.length > 0 && (
            <ul className="text-xs text-gray-600 dark:text-gray-400">
              {estimate.rows.map((r) => (
                <li key={`${r.routeId}#${r.contentType}`}>
                  {r.routeId} ({r.contentType}) - {r.deliverables} deliverable(s), {r.variants} variant(s) -{" "}
                  {fmtUsd(r.usd)}
                </li>
              ))}
            </ul>
          )}
          {estimate.unpriced > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {estimate.unpriced} deliverable(s) have no price set
              {estimate.unresolvedByReason.length > 0 &&
                ` (${estimate.unresolvedByReason.map((u) => `${u.count} ${u.reason}`).join(", ")})`}
              .
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              className={btnPrimary}
              disabled={generateBusy}
              onClick={() => void handleConfirmGenerate()}
            >
              {generateBusy ? "Generating..." : "Confirm and generate"}
            </button>
            <button
              type="button"
              className={btnGhost}
              disabled={generateBusy}
              onClick={() => setEstimate(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {progress && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Generating {progress.done} of {progress.total}...
        </p>
      )}
      {generateError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{generateError}</p>}
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {loading && items.length === 0 && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>}

      {items.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Brief</th>
                <th className="px-3 py-2">Deliverables</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {visible.map((item) => (
                <tr key={item.itemId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2">
                    <a
                      href={hashForRoute({ kind: "itemDetail", projectId, itemId: item.itemId })}
                      className="font-medium text-gray-900 hover:underline dark:text-gray-100"
                    >
                      {item.title}
                    </a>
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-gray-500 dark:text-gray-400">{item.brief}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(item.deliverableSummary).map(([key, summary]) => (
                        <StatusChip key={key} status={summary.status} label={`${key}: ${summary.status}`} />
                      ))}
                      {Object.keys(item.deliverableSummary).length === 0 && (
                        <span className="text-xs text-gray-400">none</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No items match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <button
          type="button"
          className={`${btnGhost} mt-3`}
          disabled={loading}
          onClick={() => void load(nextCursor, false)}
        >
          {loading ? "Loading..." : "Load more"}
        </button>
      )}
    </div>
  );
}

function SettingsTab({ project, onSaved }: { project: Project; onSaved: (p: Project) => void }) {
  const [styleGuide, setStyleGuide] = useState(project.styleGuide);
  const [sharedContext, setSharedContext] = useState(project.sharedContext);
  const [negativeGuidance, setNegativeGuidance] = useState(project.negativeGuidance);
  const [defaults, setDefaults] = useState(project.defaults);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [refBusy, setRefBusy] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);

  function setDefaultRoute(ct: ContentType, routeId: string) {
    setDefaults((prev) => ({ ...prev, [ct]: { ...prev[ct], routeId: routeId || undefined } }));
  }
  function setDefaultVariantCount(ct: ContentType, count: number) {
    setDefaults((prev) => ({ ...prev, [ct]: { ...prev[ct], variantCount: count } }));
  }

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      const updated = await api<Project>(`/api/projects/${project.projectId}`, {
        method: "PATCH",
        json: { styleGuide, sharedContext, negativeGuidance, defaults },
      });
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save settings.");
    } finally {
      setBusy(false);
    }
  }

  async function handleUpload(file: File) {
    setRefBusy(true);
    setRefError(null);
    try {
      const mimeType = file.type || "application/octet-stream";
      const { assetId, url, s3Key } = await api<{ assetId: string; url: string; s3Key: string }>(
        `/api/projects/${project.projectId}/refs/upload-url`,
        { method: "POST", json: { filename: file.name, mimeType } }
      );
      await apiUpload(url, file, mimeType);
      const asset = await api<ReferenceAsset>(`/api/projects/${project.projectId}/refs`, {
        method: "POST",
        json: { assetId, s3Key, mimeType, label: file.name },
      });
      onSaved({ ...project, referenceAssets: [...project.referenceAssets, asset] });
    } catch (err) {
      setRefError(err instanceof Error ? err.message : "Could not upload the reference image.");
    } finally {
      setRefBusy(false);
    }
  }

  async function handleDeleteRef(assetId: string) {
    await api(`/api/projects/${project.projectId}/refs/${assetId}`, { method: "DELETE" });
    onSaved({ ...project, referenceAssets: project.referenceAssets.filter((a) => a.assetId !== assetId) });
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Style guide</label>
        <textarea rows={3} className={inputCls} value={styleGuide} onChange={(e) => setStyleGuide(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Shared context</label>
        <textarea
          rows={3}
          className={inputCls}
          value={sharedContext}
          onChange={(e) => setSharedContext(e.target.value)}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Negative guidance</label>
        <textarea
          rows={2}
          className={inputCls}
          value={negativeGuidance}
          onChange={(e) => setNegativeGuidance(e.target.value)}
        />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Per-type defaults</h3>
        <div className="space-y-2">
          {CONTENT_TYPES.map((ct) => (
            <div key={ct} className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-sm text-gray-500 dark:text-gray-400">{ct}</span>
              <div className="min-w-56 flex-1">
                <RoutePicker contentType={ct} value={defaults[ct]?.routeId} onChange={(routeId) => setDefaultRoute(ct, routeId)} />
              </div>
              <input
                type="number"
                min={1}
                max={4}
                className="w-20 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                value={defaults[ct]?.variantCount ?? 1}
                onChange={(e) => setDefaultVariantCount(ct, Math.min(4, Math.max(1, Number(e.target.value) || 1)))}
              />
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <button type="button" className={btnPrimary} disabled={busy} onClick={() => void handleSave()}>
        {busy ? "Saving..." : "Save settings"}
      </button>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Reference images</h3>
        <div className="flex flex-wrap gap-3">
          {project.referenceAssets.map((asset) => (
            <div key={asset.assetId} className="text-center">
              <RefThumbnail projectId={project.projectId} asset={asset} />
              <button
                type="button"
                onClick={() => void handleDeleteRef(asset.assetId)}
                className="mt-1 text-xs text-red-600 hover:underline dark:text-red-400"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <input
          type="file"
          accept="image/*"
          disabled={refBusy}
          className="mt-3 text-sm text-gray-600 dark:text-gray-300"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload(file);
            e.target.value = "";
          }}
        />
        {refError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{refError}</p>}
      </div>
    </div>
  );
}

function ProjectTemplatesTab({ projectId }: { projectId: string }) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customizing, setCustomizing] = useState<string | null>(null);

  useEffect(() => {
    void api<{ items: PromptTemplate[] }>("/api/templates")
      .then((res) => setTemplates(res.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load templates."));
  }, []);

  const selected = templates.find((t) => t.templateId === selectedId) ?? null;

  async function handleCustomize(template: PromptTemplate) {
    setCustomizing(template.templateId);
    try {
      const override = await api<PromptTemplate>("/api/templates", {
        method: "POST",
        json: { ...template, projectId },
      });
      setTemplates((prev) => prev.map((t) => (t.templateId === override.templateId ? override : t)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create a project override.");
    } finally {
      setCustomizing(null);
    }
  }

  return (
    <div className="grid gap-6 sm:grid-cols-[220px_1fr]">
      <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
        {templates.map((t) => (
          <li key={t.templateId} className="flex items-center justify-between px-3 py-2">
            <button
              type="button"
              onClick={() => setSelectedId(t.templateId)}
              className={`truncate text-left text-sm ${
                t.templateId === selectedId ? "font-semibold text-(--accent)" : "text-gray-700 dark:text-gray-300"
              }`}
            >
              {t.name}
            </button>
            {!t.projectId && (
              <button
                type="button"
                disabled={customizing === t.templateId}
                onClick={() => void handleCustomize(t)}
                className="shrink-0 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Customize
              </button>
            )}
          </li>
        ))}
      </ul>
      <div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {selected ? (
          <TemplateEditor
            key={selected.templateId}
            template={selected}
            projectId={selected.projectId ? projectId : undefined}
            onSaved={(updated) =>
              setTemplates((prev) => prev.map((t) => (t.templateId === updated.templateId ? updated : t)))
            }
          />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Select a template to edit or preview it.</p>
        )}
      </div>
    </div>
  );
}

export function ProjectDetail({ projectId }: { projectId: string }) {
  const [project, setProject] = useState<Project | null>(null);
  useDocumentTitle(
    formatPageTitle(project && project.projectId === projectId ? project.name : null, ROUTE_TITLES.projectDetail),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"items" | "settings" | "templates">("items");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // There is no GET /api/projects/:id - the project detail comes from the list.
    void api<{ items: Project[] }>("/api/projects")
      .then((res) => {
        const found = res.items.find((p) => p.projectId === projectId);
        if (!found) throw new ApiError("Project not found.", 404, "NOT_FOUND");
        return found;
      })
      .then((p) => {
        if (!cancelled) setProject(p);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : "Could not load the project.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (loading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading...</p>;
  }
  if (error || !project) {
    return <p className="p-6 text-sm text-red-600 dark:text-red-400">{error ?? "Project not found."}</p>;
  }

  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: "items", label: "Items" },
    { key: "settings", label: "Settings" },
    { key: "templates", label: "Templates" },
  ];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{project.name}</h1>
        <a
          href={hashForRoute({ kind: "book", projectId: project.projectId })}
          className="text-sm text-(--accent) hover:underline"
        >
          Book view
        </a>
      </div>
      <nav className="mt-4 flex gap-1 border-b border-gray-200 dark:border-gray-800">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === t.key
                ? "border-(--accent) font-medium text-(--accent)"
                : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-4">
        {tab === "items" && <ItemsTab projectId={project.projectId} />}
        {tab === "settings" && <SettingsTab project={project} onSaved={setProject} />}
        {tab === "templates" && <ProjectTemplatesTab projectId={project.projectId} />}
      </div>
    </div>
  );
}
